import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import type { AgNodeClickEvent } from 'ag-charts-community';
import { AgCharts } from 'ag-charts-react';
import type { AgPolarChartOptions } from 'ag-charts-types';
import { useCallback, useMemo, useState } from 'react';

import {
  getAgChartsTheme,
  noDataRenderer
} from '@/components/charts/charts.utils';
import {
  donutToolTipRenderer,
  getDonutColors
} from '@/components/charts/DonutChart/DonutChart.utils';
import {
  formatLargeNumber,
  hasDataGreaterThanZero
} from '@/utils/Common.utils';

import { useChartLocationCheck } from '../charts.hooks';
import useStyles from './DonutChart.styles';
import type { DonutChartDataTypes, DonutChartProps } from './DonutChart.types';
import DonutChartLegend from './DonutChartLegend/DonutChartLegend';

const DonutChart = (props: DonutChartProps) => {
  const {
    data,
    legendPosition = 'left',
    resolveCssVariables = false,
    onChartClick
  } = props;
  const hasVisibleData = hasDataGreaterThanZero(data);
  const { mode: themeMode } = useColorScheme();
  const theme = useTheme();
  const [visibleItems, setVisibleItems] = useState<Set<string>>(
    new Set(data?.map((item) => item.name))
  );
  const [previousData, setPreviousData] = useState(data);

  if (data !== previousData) {
    setPreviousData(data);
    setVisibleItems(new Set(data?.map((item) => item.name) || []));
  }

  const { classes } = useStyles({ hasVisibleData, legendPosition });
  const onReportsPage = useChartLocationCheck();
  // inner labels and inner label font sizes (responsive based on screen size)
  const numFormatter = new Intl.NumberFormat('en-US');
  const total = useMemo(() => {
    return data
      ?.filter((item) => visibleItems.has(item.name))
      .reduce((sum, d) => sum + (d.count ?? 0), 0);
  }, [data, visibleItems]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const totalFontSize = useMemo(() => {
    if (isSmallScreen) return 12;
    if (isMediumScreen) return 14;
    return 16;
  }, [isSmallScreen, isMediumScreen]);

  const subtitleFontSize = useMemo(() => {
    if (isSmallScreen) return 7;
    if (isMediumScreen) return 8;
    return 9;
  }, [isSmallScreen, isMediumScreen]);

  // -----------------------------------------------------------------
  const handleLegendItemClick = useCallback((itemName: string) => {
    setVisibleItems((prev) => {
      const newVisible = new Set(prev);
      if (newVisible.has(itemName)) {
        // Don't allow hiding all items
        if (newVisible.size > 1) {
          newVisible.delete(itemName);
        }
      } else {
        newVisible.add(itemName);
      }
      return newVisible;
    });
  }, []);

  const enhancedData = useMemo(() => {
    return data?.map((item) => {
      const isVisible = visibleItems.has(item.name);
      // Use a simple, clean approach with consistent spacing
      const formattedValue = numFormatter.format(item.count ?? 0);
      return {
        ...item,
        // Set count to 0 for hidden items to make them invisible but keep in data structure
        count: isVisible ? item.count : 0,
        legendLabel: `${item.name}    ${formattedValue}`
      };
    });
  }, [data, numFormatter, visibleItems]);

  const colors = useMemo(() => {
    return getDonutColors(data ?? [], resolveCssVariables);
  }, [data, resolveCssVariables]);

  const options = useMemo(() => {
    if (!hasVisibleData) {
      return {
        axes: [
          {
            type: 'category',
            position: 'bottom',
            line: {
              enabled: false
            }
          },
          {
            type: 'number',
            position: 'left',
            line: {
              enabled: false
            }
          }
        ],
        background: {
          fill: 'transparent'
        },
        overlays: {
          noData: {
            renderer: () => noDataRenderer()
          }
        },
        theme: getAgChartsTheme(themeMode)
      };
    }
    if (visibleItems?.size !== 0) {
      return {
        animation: {
          enabled: true,
          duration: 800
        },
        background: {
          fill: 'transparent'
        },
        data: enhancedData,
        legend: {
          enabled: false
        },
        series: [
          {
            type: 'donut',
            angleKey: 'count',
            fills: colors,
            innerLabels: [
              {
                text: formatLargeNumber(total),
                fontSize: totalFontSize,
                color: onReportsPage ? 'black' : undefined
              },
              {
                text: 'Total',
                fontSize: subtitleFontSize,
                color: onReportsPage ? 'black' : undefined
              }
            ],
            innerRadiusRatio: 0.7,
            legendItemKey: 'legendLabel',
            listeners: {
              seriesNodeClick: (
                event: AgNodeClickEvent<
                  'seriesNodeClick',
                  DonutChartDataTypes,
                  unknown
                >
              ) => {
                if (event.datum && onChartClick) {
                  onChartClick(event);
                }
              }
            },
            outerRadiusRatio: 1,
            sectorSpacing: 2,
            strokeWidth: 0,
            tooltip: {
              renderer: donutToolTipRenderer
            }
          }
        ],
        theme: getAgChartsTheme(themeMode),
        padding: {
          top: 8,
          right: 8,
          bottom: 8,
          left: 8
        }
      } as AgPolarChartOptions<DonutChartDataTypes>;
    }
    return null;
  }, [
    colors,
    enhancedData,
    hasVisibleData,
    themeMode,
    numFormatter,
    onChartClick,
    subtitleFontSize,
    total,
    totalFontSize,
    visibleItems
  ]);

  return (
    <Box className={classes.donutChartWrapper}>
      <Box className={classes.donutChartContainer}>
        {options && (
          <AgCharts
            key={Array.from(visibleItems).sort().join(',')}
            className={classes.donutChart}
            options={options}
          />
        )}
      </Box>
      <Box className={classes.donutChartLegendContainer}>
        <DonutChartLegend
          data={data}
          colors={colors}
          numFormatter={numFormatter}
          visibleItems={visibleItems}
          onLegendItemClick={handleLegendItemClick}
        />
      </Box>
    </Box>
  );
};

DonutChart.displayName = 'DonutChart';

export default DonutChart;
